import { HttpTypes } from '@/types';
import { AxiosInstance, AxiosRequestConfig } from 'axios';

class Customer {
  private client: AxiosInstance;
  private routePath = '/customer/auth';
  private storePath = '/customer/store';
  private addressPath = '/customer/store/customer/address';
  private orderPath = '/customer/store/customer/orders';

  public host = '';

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  interceptRequest(config: AxiosRequestConfig) {
    config.headers = {
      ...config.headers,
    };

    return config;
  }

  async signin(
    credential: {
      email: string;
      password: string;
    },
    cartId?: string
  ): Promise<HttpTypes.StoreCustomerSigninResponse> {
    const result = await this.client.post(`${this.routePath}/signin`, credential, {
      headers: {
        Origin: this.host,
        'Content-Type': 'application/json',
        'X-Cart-Id': cartId,
      },
    });
    return result.data;
  }

  async forgotPassword(credential: {
    email: string;
  }): Promise<{ success: boolean; statusCode?: number; message?: string }> {
    const result = await this.client.post(`${this.routePath}/forgot-password`, credential, {
      headers: {
        Origin: this.host,
        'Content-Type': 'application/json',
      },
    });
    return result.data;
  }

  async resetPassword(credential: {
    token: string;
    password: string;
  }): Promise<{ success: boolean; statusCode?: number; message?: string }> {
    const result = await this.client.post(`${this.routePath}/reset-password`, credential, {
      headers: {
        Origin: this.host,
        'Content-Type': 'application/json',
      },
    });
    return result.data;
  }

  async signup(body: HttpTypes.StoreCustomerSignup): Promise<{ status: boolean; message: string }> {
    const result = await this.client.post(`${this.routePath}/signup`, body, {
      headers: {
        Origin: this.host,
        'Content-Type': 'application/json',
      },
    });
    return result.data;
  }

  async me(token: string): Promise<HttpTypes.StoreCustomerMeResponse> {
    const result = await this.client.post(
      `${this.routePath}/me`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Origin: this.host,
          'Content-Type': 'application/json',
        },
      }
    );
    return result.data;
  }

  async update(token: string, body: HttpTypes.StoreCustomerProfilePayload) {
    const result = await this.client.put(`${this.storePath}/profile`, body, {
      headers: {
        Authorization: `Bearer ${token}`,
        Origin: this.host,
        'Content-Type': 'application/json',
        'X-Jstore-Host': this.host,
      },
    });

    return result.data;
  }

  async detail(token: string): Promise<HttpTypes.StoreCustomerDetailResponse> {
    const result = await this.client.get(`${this.storePath}/profile`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Origin: this.host,
      },
    });
    return result.data;
  }

  async verify(token: string): Promise<HttpTypes.StoreCustomerVerifyEmailResponse> {
    const result = await this.client.post(
      `${this.routePath}/verify-email`,
      {
        token,
      },
      {
        headers: {
          Origin: this.host,
          'Content-Type': 'application/json',
          'X-Jstore-Host': this.host,
        },
      }
    );
    return result.data;
  }

  async verifyToken(token: string): Promise<HttpTypes.StoreCustomerVerifyEmailResponse> {
    const payload = {
      token: token,
    };

    const result = await this.client.post(`${this.routePath}/verify-token`, payload, {
      headers: {
        Origin: this.host,
        'Content-Type': 'application/json',
      },
    });

    return result.data;
  }

  async refreshToken(
    token: HttpTypes.StoreCustomerRefreshToken
  ): Promise<HttpTypes.StoreCustomerRefreshTokenResponse> {
    this.interceptRequest({ ...this.client.request });
    const result = await this.client.post(`${this.routePath}/refresh`, token);
    return result.data;
  }

  async listAddress(token: string): Promise<HttpTypes.StoreCustomerAddressListResponse> {
    const result = await this.client.get(`${this.addressPath}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Origin: this.host,
        'Content-Type': 'application/json',
        'X-Jstore-Host': this.host,
      },
    });
    return result.data;
  }

  async createAddress(token: string, body: HttpTypes.StoreCustomerAddressPayload) {
    const result = await this.client.post(`${this.addressPath}`, body, {
      headers: {
        Authorization: `Bearer ${token}`,
        Origin: this.host,
        'Content-Type': 'application/json',
        'X-Jstore-Host': this.host,
      },
    });

    return result.data;
  }

  async updateAddress(
    token: string,
    addressId: string,
    body: HttpTypes.StoreCustomerAddressPayload
  ) {
    const result = await this.client.put(`${this.addressPath}/${addressId}`, body, {
      headers: {
        Authorization: `Bearer ${token}`,
        Origin: this.host,
        'Content-Type': 'application/json',
        'X-Jstore-Host': this.host,
      },
    });

    return result.data;
  }

  async deleteAddress(token: string, addressId: string) {
    const result = await this.client.delete(`${this.addressPath}/${addressId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Origin: this.host,
      },
    });

    return result.data;
  }

  async setDefaultAddress(token: string, addressId: string) {
    const result = await this.client.put(
      `${this.addressPath}/default`,
      {
        address_id: addressId,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Origin: this.host,
          'Content-Type': 'application/json',
          'X-Jstore-Host': this.host,
        },
      }
    );

    return result.data;
  }

  // Email
  async resendVerificationEmail(body: {
    email: string;
  }): Promise<HttpTypes.StoreCustomerSigninResponse> {
    const result = await this.client.post(
      `${this.routePath}/resend-verification-email`,
      { email: body.email },
      {
        headers: {
          Origin: this.host,
          'Content-Type': 'application/json',
        },
      }
    );
    return result.data;
  }

  // Order

  async listOrder(
    token: string,
    params: HttpTypes.FindParams
  ): Promise<HttpTypes.StoreCustomerOrderListResponse> {
    const result = await this.client.get(`${this.orderPath}/history`, {
      params: params,
      headers: {
        Authorization: `Bearer ${token}`,
        Origin: this.host,
        'Content-Type': 'application/json',
        'X-Jstore-Host': this.host,
      },
    });
    return result.data;
  }

  async detailOrder(
    token: string,
    id: string
  ): Promise<HttpTypes.StoreCustomerOrderDetailResponse> {
    const result = await this.client.get(`${this.orderPath}/detail/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Origin: this.host,
        'Content-Type': 'application/json',
        'X-Jstore-Host': this.host,
      },
    });
    return result.data;
  }

  async listReview(
    token: string,
    params: HttpTypes.FindParams
  ): Promise<HttpTypes.StoreCustomerReviewListResponse> {
    const result = await this.client.get(`${this.storePath}/customer/reviews`, {
      params: params,
      headers: {
        Authorization: `Bearer ${token}`,
        Origin: this.host,
        'Content-Type': 'application/json',
        'X-Jstore-Host': this.host,
      },
    });

    return result.data;
  }

  async createReview(
    token: string,
    body: HttpTypes.StoreCustomerReviewCreatePayload
  ): Promise<HttpTypes.StoreCustomerReviewCreateResponse> {
    const result = await this.client.post(`${this.storePath}/customer/reviews`, body, {
      headers: {
        Authorization: `Bearer ${token}`,
        Origin: this.host,
        'Content-Type': 'application/json',
        'X-Jstore-Host': this.host,
      },
    });

    return result.data;
  }

  async listUnReview(
    token: string,
    params: HttpTypes.FindParams
  ): Promise<HttpTypes.StoreCustomerUnReviewListResponse> {
    const result = await this.client.get(`${this.storePath}/customer/unreviews`, {
      params: params,
      headers: {
        Authorization: `Bearer ${token}`,
        Origin: this.host,
        'Content-Type': 'application/json',
        'X-Jstore-Host': this.host,
      },
    });

    return result.data;
  }
}

export default Customer;
